import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Flyout } from "../../../../../../components/global_components/Flyout";
import { buildNotifyContactQueryKey, useNotifyContact } from "../../../../hooks/useNotifyContact";
import { ContactAvatar } from "../../../../../permits/components/ContactAvatar";

import { NotifyContactSubscriberContactMethodRow } from "../../notifications/components/NotifyContactSubscriberContactMethodRow";
import { Button, Checkbox, Form, Segment } from "semantic-ui-react";
import { faCirclePlus, faUserMinus, faUserPlus } from "@fortawesome/pro-solid-svg-icons";
import { AdminAddSubscriberContactMethodModal } from "../modals/AdminAddSubscriberContactMethodModal";
import { useNotifySubscriptionGroups } from "../../../../hooks/useNotifySubscriptionGroups";
import { chain } from "lodash";
import { SubscriptionGroupSublabel } from "../../notifications/components/SubscriptionGroupSublabel";
import { useForm, useWatch } from "react-hook-form";
import { ControlledCheckbox } from "../../../../../../components/ControlledForm";
import PlaceholderUntilLoaded from "../../../../../../components/global_components/PlaceholderUntilLoaded";
import { buildAppIdentifierString } from "../utils/buildAppIdentifierString";
import { buildIdentifierString } from "../utils/buildIdentifierString";
import { useBulkNotifySubscriptionPreferencesUpdate } from "../../../../hooks/useNotifyContactPreferenceUpdate";
import { ToastStatuses } from "../../../../../../components/global_components/Toast";
import { useToasts } from "react-toast-notifications";
import { useQueryCache } from "react-query";
import { NotifySubscriptionGroupCollapsibleSection } from "../../subscription-groups/components/NotifySubscriptionGroupCollapsibleSection";
import { useNotifySubscriptionPreferencesUpdate } from "../../../../hooks/useNotifySubscriptionPreferencesUpdate";
import { RemoveNotifyContactModal } from "../modals/RemoveNotifyContactModal";

type FormType = {
  subscriptionGroups: Record<
    string,
    {
      method: Record<string, boolean>;
    }
  >;
};
export function NotifyContactDetailFlyout({
  notifyContactId,
  onClose,
}: {
  notifyContactId?: number | null;
  onClose: () => void;
}) {
  const [isAddContactMethodModalOpen, setIsAddContactMethodModalOpen] = useState(false);
  const [isRemoveNotifyContactFromGroupModalOpen, setIsRemoveNotifyContactFromGroupModalOpen] = useState(false);
  const [removalModalData, setRemovalModalData] = useState<{
    subscriptionGroupId: number | null;
    subscriberContactMethodIds: number[] | null;
  }>({ subscriptionGroupId: null, subscriberContactMethodIds: null });

  const { notifyContact, isNotifyContactLoading } = useNotifyContact({
    id: notifyContactId,
  });

  const queryCache = useQueryCache();

  const { subscriptionGroups, isSubscriptionGroupsLoading } = useNotifySubscriptionGroups({});

  const { control, handleSubmit, reset, setValue } = useForm<FormType>({
    defaultValues: {
      subscriptionGroups: {},
    },
  });

  const formValues = useWatch({ control });
  const { addToast } = useToasts();

  // Add Contact method to private/Unlisted Group
  const { editSubscriptionPreferences } = useNotifySubscriptionPreferencesUpdate({
    onSuccess: async () => {
      addToast(`Contact Methods Added to Group`, {
        appearance: ToastStatuses.Success,
        autoDismiss: true,
      });
      queryCache.invalidateQueries(buildNotifyContactQueryKey({ id: notifyContactId }));
    },
  });

  // Buk update for modifying subscription preferences
  const { executeBulkUpdate } = useBulkNotifySubscriptionPreferencesUpdate({
    onSuccess: () => {
      addToast(`Subscription Preferences Updated`, { appearance: ToastStatuses.Success, autoDismiss: true });
      queryCache.invalidateQueries(buildNotifyContactQueryKey({ id: notifyContactId }));
      onClose();
    },
    onError: () => {
      addToast(`Subscription Preferences Failed to Update`, { appearance: ToastStatuses.Error, autoDismiss: true });
    },
  });

  // Set default form values
  useEffect(() => {
    if (!notifyContact?.contactMethods || !subscriptionGroups) return;

    const defaultSubscriptionGroups: FormType["subscriptionGroups"] = {};

    subscriptionGroups.forEach((group) => {
      const groupKey = `group_${group.id}`; // Prefix to ensure it's treated as string
      defaultSubscriptionGroups[groupKey] = { method: {} };

      notifyContact.contactMethods.forEach((contactMethod) => {
        const methodKey = `method_${contactMethod.id}`; // Prefix to ensure it's treated as string

        const preference = contactMethod.subscriptionPreferences?.find((pref) => pref.subscriptionGroupId === group.id);

        let isSubscribed: boolean;

        if (group.autoSubscribe) {
          isSubscribed = preference ? preference.type !== "Unsubscribe" : true;
        } else {
          isSubscribed = preference ? preference.type === "Subscribe" : false;
        }

        defaultSubscriptionGroups[groupKey].method[methodKey] = isSubscribed;
      });
    });

    reset({ subscriptionGroups: defaultSubscriptionGroups });

    reset({ subscriptionGroups: defaultSubscriptionGroups });
  }, [notifyContact]);

  const onSave = (data: FormType) => {
    if (!notifyContact?.contactMethods || !subscriptionGroups) return;

    // Build current state
    const getCurrentPreferences = () => {
      const current: Record<string, Record<string, boolean>> = {};

      notifyContact.contactMethods.forEach((contactMethod) => {
        const methodKey = `method_${contactMethod.id}`;
        current[methodKey] = {};

        subscriptionGroups.forEach((group) => {
          const groupKey = `group_${group.id}`;
          const preference = contactMethod.subscriptionPreferences?.find(
            (pref) => pref.subscriptionGroupId === group.id,
          );

          // Determine current value (same logic as initialization)
          let currentValue: boolean;
          if (group.autoSubscribe) {
            currentValue = preference ? preference.type !== "Unsubscribe" : true;
          } else {
            currentValue = preference ? preference.type === "Subscribe" : false;
          }

          current[methodKey][groupKey] = currentValue;
        });
      });

      return current;
    };

    const currentPreferences = getCurrentPreferences();

    const changedPreferences: Array<{
      contactMethodId: number;
      subscriptionGroupId: number;
      type: "Subscribe" | "Unsubscribe";
    }> = [];

    Object.entries(data.subscriptionGroups).forEach(([groupKey, groupData]) => {
      const groupId = Number(groupKey.replace("group_", ""));

      Object.entries(groupData.method).forEach(([methodKey, newValue]) => {
        const contactMethodId = Number(methodKey.replace("method_", ""));

        const currentValue = currentPreferences[methodKey]?.[groupKey];

        if (currentValue !== newValue) {
          changedPreferences.push({
            contactMethodId,
            subscriptionGroupId: groupId,
            type: newValue ? "Subscribe" : "Unsubscribe",
          });
        }
      });
    });

    // Group by contact method and type
    const groupedByMethodAndType = chain(changedPreferences)
      .groupBy((pref) => `${pref.contactMethodId}_${pref.type}`)
      .map((prefs) => ({
        subscriberContactMethodIds: [prefs[0].contactMethodId],
        subscriptionGroupIds: prefs.map((p) => p.subscriptionGroupId),
        type: prefs[0].type,
        overwriteExistingPreference: true,
      }))
      .value();

    executeBulkUpdate(groupedByMethodAndType);
  };

  return (
    <>
      <AdminAddSubscriberContactMethodModal
        open={isAddContactMethodModalOpen}
        onClose={() => {
          setIsAddContactMethodModalOpen(false);
        }}
      />
      <RemoveNotifyContactModal
        subscriberContactMethodIds={removalModalData.subscriberContactMethodIds || undefined}
        group={subscriptionGroups?.find((g) => g.id === removalModalData.subscriptionGroupId) || null}
        isOpen={isRemoveNotifyContactFromGroupModalOpen}
        close={() => {
          setIsRemoveNotifyContactFromGroupModalOpen(false);
          queryCache.invalidateQueries(buildNotifyContactQueryKey({ id: notifyContactId }));
          close();
        }}
      />
      <Flyout open={!!notifyContactId} onClose={onClose}>
        <Flyout.Header>
          <PlaceholderUntilLoaded isLoading={isNotifyContactLoading || isSubscriptionGroupsLoading}>
            {notifyContact && notifyContact.person && (
              <div className='tw-flex tw-items-center tw-gap-2' key={notifyContact.id}>
                <ContactAvatar
                  contact={{
                    firstName: notifyContact.person.firstName,
                    lastName: notifyContact.person.lastName,
                    contactType: null,
                    userId: notifyContact.person.id,
                  }}
                  size='40px'
                />
                <span className='tw-text-[20px] tw-font-bold'>{notifyContact.person.fullName}</span>
              </div>
            )}
          </PlaceholderUntilLoaded>
        </Flyout.Header>
        <Flyout.Body>
          <PlaceholderUntilLoaded isLoading={isNotifyContactLoading || isSubscriptionGroupsLoading}>
            {notifyContact?.contactMethods.map((method) => (
              <NotifyContactSubscriberContactMethodRow
                subscriberContactMethod={method}
                disabled={method.status != "Enrolled"}
                key={method.id}
                notifyContactId={notifyContact.id}
              />
            ))}
            <button
              className='tw-utility-button tw-w-full tw-text-primary'
              onClick={() => setIsAddContactMethodModalOpen((prev) => !prev)}
            >
              <Segment className='tw-flex tw-w-full tw-font-bold'>
                <FontAwesomeIcon className='tw-mr-2 tw-text-primary' icon={faCirclePlus} />
                Add Contact Method
              </Segment>
            </button>

            <div title='Notify Contacts Subscription Preferences Management Section'>
              {subscriptionGroups?.map((subscriptionGroup) => {
                const groupKey = `group_${subscriptionGroup.id}`; // Prefix to ensure it's treated as string
                const allChecked = notifyContact?.contactMethods.every((contactMethod) => {
                  const methodKey = `method_${contactMethod.id}`; // Prefix to ensure it's treated as string
                  const fieldValue = formValues.subscriptionGroups?.[groupKey]?.method?.[methodKey];
                  return fieldValue === true;
                });

                const handleAllCheckboxChange = (checked: boolean) => {
                  notifyContact?.contactMethods.forEach((contactMethod) => {
                    setValue(
                      `subscriptionGroups.group_${subscriptionGroup.id}.method.method_${contactMethod.id}`,
                      checked,
                    );
                  });
                };

                // Determine if the group has any contact methods with preferences
                const hasPreferences = notifyContact?.contactMethods.some((contactMethod) =>
                  contactMethod.subscriptionPreferences?.some(
                    (preference) => preference.subscriptionGroupId === subscriptionGroup.id,
                  ),
                );

                const isGroupEligibleForIcon = subscriptionGroup.visibility !== "Public";

                const icon = isGroupEligibleForIcon ? (
                  hasPreferences ? (
                    <FontAwesomeIcon
                      icon={faUserMinus}
                      className='tw-cursor-pointer tw-text-red'
                      onClick={() => {
                        setRemovalModalData({
                          subscriptionGroupId: subscriptionGroup.id,
                          subscriberContactMethodIds: notifyContact?.contactMethods.map((cm) => cm.id) || null,
                        });
                        setIsRemoveNotifyContactFromGroupModalOpen(true);
                      }}
                    />
                  ) : (
                    <FontAwesomeIcon
                      icon={faUserPlus}
                      className='tw-cursor-pointer tw-text-primary-hint'
                      onClick={(e) => {
                        e.stopPropagation();
                        editSubscriptionPreferences({
                          subscriberContactMethodIds: notifyContact
                            ? [...notifyContact.contactMethods.map((cm) => cm.id)]
                            : [],
                          subscriptionGroupIds: [subscriptionGroup.id],
                          type: "Subscribe",
                          overwriteExistingPreference: true,
                        });
                      }}
                    />
                  )
                ) : null;

                return (
                  <NotifySubscriptionGroupCollapsibleSection
                    title={subscriptionGroup.name}
                    titleSubLabel={<SubscriptionGroupSublabel group={subscriptionGroup} />}
                    icon={icon}
                    key={subscriptionGroup.id}
                  >
                    <Checkbox
                      checked={allChecked}
                      onChange={(_, data) => handleAllCheckboxChange(!!data.checked)}
                      label='All contact methods'
                    />

                    {notifyContact?.contactMethods.map((contactMethod) => {
                      const fieldValue =
                        formValues.subscriptionGroups?.[subscriptionGroup.id]?.method?.[contactMethod.id];
                      return (
                        <Form.Field className='tw-m-0' key={`${subscriptionGroup.id}-${contactMethod.id}`}>
                          <ControlledCheckbox
                            control={control}
                            key={`notifyContact.${notifyContact.id}.subscriptionGroups.${subscriptionGroup.id}.method.${contactMethod.id}`}
                            name={`subscriptionGroups.group_${subscriptionGroup.id}.method.method_${contactMethod.id}`}
                            label={
                              contactMethod.type === "App"
                                ? buildAppIdentifierString(
                                    contactMethod.metadata.platformDetails,
                                    contactMethod.metadata.deviceType,
                                  )
                                : buildIdentifierString(contactMethod)
                            }
                            defaultValue={fieldValue}
                          />
                        </Form.Field>
                      );
                    })}
                  </NotifySubscriptionGroupCollapsibleSection>
                );
              })}
            </div>
          </PlaceholderUntilLoaded>
        </Flyout.Body>
        <Flyout.Footer>
          <div className='tw-flex tw-justify-between'>
            <Button className='button-ghost' content='Close' onClick={onClose} />
            <div className='tw-flex tw-gap-3'>
              <Button
                className='tw-m-0'
                primary
                content='Save'
                disabled={false}
                loading={false}
                onClick={handleSubmit(onSave)}
              />
            </div>
          </div>
        </Flyout.Footer>
      </Flyout>
    </>
  );
}
